<script lang="ts">
	import type { FaqEntry } from './structuredData';

	// Reusable FAQ accordion (look shared with the /wissen pages). The rendered
	// text MUST stay identical to the FAQPage schema (Google guideline): callers
	// pass the SAME FaqEntry[] here and into buildFaqPage(), so the visible
	// answers and the rich result share one source and can't drift apart.
	//
	// Answers may carry inline HTML (e.g. <strong>) and are rendered via {@html};
	// buildFaqPage() strips that markup so the schema stays plain text.
	let { entries, class: className = 'mt-12' }: { entries: FaqEntry[]; class?: string } = $props();
</script>

<section class={className}>
	<div class="faq-list">
		{#each entries as entry}
			<details class="faq-item">
				<summary>
					<h3 class="faq-q">{entry.question}</h3>
					<svg
						class="faq-chev"
						width="18"
						height="18"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2.2"
						stroke-linecap="round"
						stroke-linejoin="round"
						aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg
					>
				</summary>
				<div class="faq-answer">{@html entry.answer}</div>
			</details>
		{/each}
	</div>
</section>

<style>
	/* FAQ — native <details> accordion. Uses the global design tokens from
	   app.css; identical look to the /wissen article pages. */
	.faq-list {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}
	.faq-item {
		border: 1px solid var(--color-line);
		border-radius: var(--radius);
		background: var(--color-surface);
		box-shadow: var(--shadow-sm);
		overflow: hidden;
		transition: border-color 0.2s;
	}
	.faq-item[open] {
		border-color: color-mix(in oklch, var(--color-accent) 30%, var(--color-line));
	}
	.faq-item summary {
		display: flex;
		align-items: center;
		gap: 14px;
		cursor: pointer;
		list-style: none;
		padding: 16px 20px;
		font-family: var(--font-display);
		font-weight: 600;
		font-size: 15.5px;
		letter-spacing: -0.01em;
		color: var(--color-ink);
	}
	.faq-item summary::-webkit-details-marker {
		display: none;
	}
	.faq-item summary:hover {
		color: var(--color-accent-strong);
	}
	/* Question stays an <h3> for document outline / SEO, but inherits the
	   summary's typography instead of the surrounding article's h3 styles. */
	.faq-q {
		margin: 0;
		font: inherit;
	}
	.faq-chev {
		margin-left: auto;
		flex: none;
		color: var(--color-ink-faint);
		transition:
			transform 0.2s,
			color 0.2s;
	}
	.faq-item[open] .faq-chev {
		transform: rotate(90deg);
		color: var(--color-accent);
	}
	.faq-answer {
		padding: 0 20px 18px;
		font-size: 14.5px;
		line-height: 1.6;
		color: var(--color-ink-soft);
		text-wrap: pretty;
	}
	/* Answers come in via {@html}, so inline links need :global to be styled */
	.faq-answer :global(a) {
		color: var(--color-link);
		font-weight: 600;
	}
	.faq-answer :global(a:hover) {
		text-decoration: underline;
	}
</style>
